import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, FileText, Check, AlertCircle, Scale, ChevronRight } from 'lucide-react';

interface ConsentGateProps {
  onAccept: (flags: { location: boolean; clinical: boolean; ai: boolean }) => void;
  isProvider: boolean;
}

const ConsentGate: React.FC<ConsentGateProps> = ({ onAccept, isProvider }) => {
  const [step, setStep] = useState(1);
  const [flags, setFlags] = useState({ location: true, clinical: true, ai: true });

  const totalSteps = 2;

  const handleFinish = () => {
    onAccept(flags);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl w-full bg-slate-900 border border-white/5 rounded-[40px] shadow-2xl overflow-hidden relative"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-500/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="p-8 md:p-12 relative z-10">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-500/10 border border-brand-500/20 rounded-2xl flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-brand-500" />
              </div>
              <div>
                <h3 className="font-black text-xl tracking-tight text-white uppercase">Compliance Protocol</h3>
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em]">Step {step} of {totalSteps}</p>
              </div>
            </div>
            <div className="flex gap-1.5">
              {[1, 2].map(s => (
                <div key={s} className={`h-1.5 rounded-full transition-all ${s === step ? 'w-10 bg-brand-500' : 'w-3 bg-white/5'}`} />
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="space-y-4">
                  <h4 className="text-2xl font-bold flex items-center gap-3 text-white">
                    <Scale className="w-6 h-6 text-brand-400" />
                    Kenya Patients' Rights Charter
                  </h4>
                  <div className="bg-slate-950/50 border border-white/5 rounded-3xl p-6 h-64 overflow-y-auto text-slate-400 text-sm leading-relaxed scrollbar-thin scrollbar-thumb-white/10">
                    <p className="font-bold text-white mb-4">Under the Digital Health Act 2023 & DPA 2019:</p>
                    <ul className="space-y-4">
                      <li className="flex gap-3">
                        <div className="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-500 mt-2" />
                        <span>Right to access your digital health information at any time.</span>
                      </li>
                      <li className="flex gap-3">
                        <div className="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-500 mt-2" />
                        <span>Right to explicit granular consent before any data processing.</span>
                      </li>
                      <li className="flex gap-3">
                        <div className="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-500 mt-2" />
                        <span>Right to privacy and confidentiality during physiotherapist selection.</span>
                      </li>
                      <li className="flex gap-3">
                        <div className="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-500 mt-2" />
                        <span>Right to be informed about how AI recommendation models use your biometric and condition tags.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-2xl flex items-center gap-4">
                  <Check className="w-5 h-5 text-emerald-500 shrink-0" />
                  <p className="text-xs text-emerald-500/80 font-medium italic leading-tight">
                    By proceeding, you acknowledge having read the Kenya National Patients' Rights Charter.
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <h4 className="text-2xl font-bold text-white">Data Control Center</h4>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-5 bg-slate-950/50 border border-white/5 rounded-3xl group hover:border-brand-500/30 transition-all cursor-pointer" onClick={() => setFlags({...flags, location: !flags.location})}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${flags.location ? 'bg-brand-500/20 text-brand-500' : 'bg-slate-800 text-slate-500'}`}>
                        <Check className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-white">Location Matching</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Uber-scale proximity dispatch</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 bg-slate-950/50 border border-white/5 rounded-3xl group hover:border-brand-500/30 transition-all cursor-pointer" onClick={() => setFlags({...flags, ai: !flags.ai})}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${flags.ai ? 'bg-brand-500/20 text-brand-500' : 'bg-slate-800 text-slate-500'}`}>
                        <Check className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-white">AI-Powered Recommendations</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">RAG-based matching engine</p>
                      </div>
                    </div>
                  </div>

                  {isProvider && (
                    <div className="flex items-center justify-between p-5 bg-slate-950/50 border border-white/5 rounded-3xl group hover:border-brand-500/30 transition-all cursor-pointer" onClick={() => setFlags({...flags, clinical: !flags.clinical})}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${flags.clinical ? 'bg-brand-500/20 text-brand-500' : 'bg-slate-800 text-slate-500'}`}>
                        <Check className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-white">Professional Code of Conduct</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Ethics & Professional standards</p>
                      </div>
                    </div>
                  </div>
                  )}
                </div>

                <div className="flex items-center gap-3 p-4 bg-amber-500/5 border border-amber-500/10 rounded-2xl">
                  <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                  <p className="text-[10px] text-amber-500/80 font-bold italic leading-tight">
                    Immutable audit logs will capture this consent for DPA audit readiness (ODPC).
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-12">
            {step < totalSteps ? (
              <button 
                onClick={() => setStep(step + 1)}
                className="w-full py-5 bg-brand-500 text-white rounded-3xl font-black text-sm uppercase tracking-[0.3em] flex items-center justify-center gap-3 shadow-2xl shadow-brand-500/20 active:scale-95 transition-all"
              >
                Continue Protocol <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button 
                onClick={handleFinish}
                className="w-full py-5 bg-brand-500 text-white rounded-3xl font-black text-sm uppercase tracking-[0.3em] flex items-center justify-center gap-3 shadow-2xl shadow-brand-500/20 active:scale-95 transition-all"
              >
                Authorize & Finalize <ShieldCheck className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ConsentGate;
